import React from 'react';
import styled from 'styled-components/macro';
import Select from 'react-select';
import { useOnClickOutside } from 'lib/utils';
import {
  CheckboxInput,
  LoadingIndicator,
  Modal,
  PrimaryButton,
  Search,
} from 'lib/components';
import CloseIcon from 'lib/images/CloseIcon';
import DropdownDownTiny from 'lib/images/DropdownDownTiny';
import { theme } from 'lib/style';
import { fetchCustomerUsers } from 'lib/api';
import { useAuth } from 'lib/context';

const Header = styled.div`
  display: flex;
`;

const HeaderTitle = styled.div`
  ${theme.fontBold700}
  font-size: ${theme.fontSizes.lg};
  color: ${theme.palette.themeDark};
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  color: ${theme.palette.white};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Content = styled.div`
  width: 800px;
  margin-top: 32px;
`;

const Menu = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SelectionCountWrapper = styled.div`
  display: flex;
`;

const SelectionCountText = styled.div`
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: #272a32;
  margin-left: 16px;
  display: flex;
  align-items: center;
`;

const Counter = styled.div`
  font-size: 16px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: #272a32;
  margin-left: 16px;
`;

const UsersList = styled.div`
  margin-top: 20px;
  height: 300px;
  overflow-y: auto;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  border-top: 1px solid #eeeff2;
  padding: 16px 0;
`;

const UserTitle = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: #272a32;
  margin-left: 32px;
  cursor: pointer;
`;

const Footer = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
`;

const SelectInput = styled(Select)`
  position: relative;
  display: block;
  width: 250px;
  font-size: 16px;
  height: 40px;
  box-sizing: border-box;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

type Props = {
  selectedCustomerId?: string;
  initUsers?: string[];
  handleModalClose: (reportId?: string) => void;
  onSelect: (userIds: string[]) => void;
};

type Selection = {
  [key: string]: boolean;
};

type Department = {
  label: string;
  value: string;
};

export const UsersSelectorModal = (props: Props) => {
  const {
    handleModalClose,
    initUsers = [],
    onSelect,
    selectedCustomerId,
  } = props;
  const {
    userData: { customerId: currentCustomerId },
  } = useAuth();
  const customerId = selectedCustomerId || currentCustomerId;
  const [loading, setLoading] = React.useState(true);
  const [users, setUsers] = React.useState([]);
  const [filteredUsers, setFilteredUsers] = React.useState([]);
  const [selectedUsers, setSelectedUsers] = React.useState({} as Selection);
  const [selectAll, setSelectAll] = React.useState(false);
  const [departmentInputValue, onDepartmentInputChange] = React.useState('');
  const [departments, setDepartments] = React.useState([] as Department[]);
  const [selectedDepartments, setSelectedDepartments] = React.useState(
    [] as Department[]
  );
  const [searchQuery, setSearchQuery] = React.useState('');

  const { ref } = useOnClickOutside(handleModalClose);

  const escPress = (key: string) => {
    if (key === 'Escape') {
      handleModalClose();
    }
  };

  React.useEffect(() => {
    document.addEventListener('keyup', e => escPress(e.key));

    return () =>
      document.removeEventListener('keyup', e => escPress(e.key), true);
  }, []);

  React.useEffect(() => {
    const selected = { ...selectedUsers };

    if (initUsers.length) {
      initUsers.forEach((userId: string) => {
        selected[userId] = true;
      });

      setSelectedUsers(selected);
    }
  }, [initUsers]);

  React.useEffect(() => {
    onSearch(searchQuery || '');
  }, [selectedDepartments]);

  const fetchUsers = async () => {
    setLoading(true);
    const users = await fetchCustomerUsers(customerId);
    users.sort((userA: any, userB: any) => {
      if (userA.firstName < userB.firstName) {
        return -1;
      }
      if (userA.firstName > userB.firstName) {
        return 1;
      }
      return 0;
    });
    const departmentObject = {};
    users.forEach((user: any) => {
      if (user.Dept) {
        // @ts-ignore
        departmentObject[user.Dept] = true;
      }
    });

    setDepartments(
      Object.keys(departmentObject).map((department: string) => ({
        label: department,
        value: department,
      }))
    );
    setUsers(users);
    setFilteredUsers(users);
    setLoading(false);
  };

  React.useEffect(() => {
    fetchUsers();
  }, []);

  const onSearch = (query: string = '') => {
    query = query.trim().toLowerCase();
    const departments = selectedDepartments.map(department => department.value);
    setSearchQuery(query);
    if (!query && !departments.length) {
      setFilteredUsers(users);
      return;
    }
    const data = users.filter((user: any) => {
      if (departments.length && !departments.includes(user.Dept)) {
        return false;
      }

      const nameArray = query.split(' ');
      const namePart1 = nameArray[0];
      const namePart2 = nameArray.length > 1 ? nameArray[1] : '';
      const userFullName = `${user.firstName.toLowerCase()} ${user.lastName.toLowerCase()}`;
      return (
        userFullName.includes(namePart1) ||
        (namePart2 && userFullName.includes(namePart2))
      );
    });
    setFilteredUsers(data);
  };

  const toggleUser = (userId: string) => {
    const selected = { ...selectedUsers };

    if (selected[userId]) {
      delete selected[userId];
    } else {
      selected[userId] = true;
    }
    setSelectedUsers(selected);
  };

  const toggleSelectAll = () => {
    const value = !selectAll;
    const selected = { ...selectedUsers };
    if (value) {
      filteredUsers.forEach((user: any) => {
        selected[user.id] = true;
      });
    } else {
      filteredUsers.forEach((user: any) => {
        delete selected[user.id];
      });
    }
    setSelectAll(value);
    setSelectedUsers(selected);
  };

  const onChangeDepartments = (newDepartments: Department[]) => {
    setSelectedDepartments(newDepartments || []);
  };

  const count = Object.keys(selectedUsers).length;

  return (
    <Modal style={{ position: 'fixed' }}>
      <div ref={ref} style={{ padding: '32px' }}>
        <Header>
          <HeaderTitle>New Custom Report</HeaderTitle>
          <CloseButtonWrap title={'Close'}>
            <CloseIcon
              onClick={() => handleModalClose()}
              width={24}
              height={24}
              color={theme.palette.label}
            />
          </CloseButtonWrap>
        </Header>
        <Content>
          {loading && <LoadingIndicator isLoading={loading} height='300px' />}
          {!loading && (
            <>
              <Menu>
                <SelectionCountWrapper>
                  <CheckboxInput
                    checked={selectAll}
                    onChange={toggleSelectAll}
                  />
                  <DropdownDownTiny color={theme.palette.label} />
                  <SelectionCountText>
                    <div>Users selected:</div>
                    <Counter>{count}</Counter>
                  </SelectionCountText>
                </SelectionCountWrapper>
                <div style={{ width: '300px' }}>
                  <Search placeholder='Search users...' onSearch={onSearch} />
                </div>
                <SelectInput
                  inputValue={departmentInputValue}
                  onInputChange={onDepartmentInputChange}
                  styles={{
                    indicatorsContainer: () => ({ display: 'none' }),
                    control: (base: any) => ({
                      ...base,
                      height: '40px',
                      border: '1px solid #D0D3D9',
                      borderRadius: '4px',
                      cursor: 'text',
                      backgroundColor: '#FFFFFF',
                      boxShadow: 'none',
                      '&:hover': {
                        border: '1px solid #D0D3D9',
                      },
                    }),
                    valueContainer: (base: any) => ({
                      ...base,
                      overflow: 'auto',
                      height: '40px',
                    }),
                    menu: (base: any) => ({
                      ...base,
                      maxHeight: '200px',
                    }),
                    menuList: (base: any) => ({
                      ...base,
                      maxHeight: '200px',
                    }),
                    placeholder: (base: any) => ({
                      ...base,
                      opacity: '0.6',
                      color: theme.palette.black_1_100,
                      fontFamily: theme.systemFontStack,
                      display: 'block',
                    }),
                  }}
                  placeholder={'Search for Departments'}
                  value={selectedDepartments}
                  onChange={onChangeDepartments}
                  options={departments}
                  noOptionsMessage={() => null}
                  isMulti={true}
                  clearable={true}
                  optionHeight={40}
                />
              </Menu>
              <UsersList>
                {filteredUsers.map((user: any) => (
                  <UserItem key={user.id}>
                    <CheckboxInput
                      checked={!!selectedUsers[user.id]}
                      onChange={() => {
                        toggleUser(user.id);
                      }}
                    />

                    <UserTitle
                      onClick={() => {
                        toggleUser(user.id);
                      }}
                    >
                      {`${user.firstName} ${user.lastName}`}
                    </UserTitle>
                  </UserItem>
                ))}
              </UsersList>
              <Footer>
                <PrimaryButton
                  text='Continue'
                  textColor='white'
                  borderRadius={5}
                  fontSize='15px'
                  textPadding='8px 16px'
                  onClick={() => {
                    onSelect(Object.keys(selectedUsers));
                  }}
                  disabled={!count}
                />
              </Footer>
            </>
          )}
        </Content>
      </div>
    </Modal>
  );
};
